import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableHeadCell,
  TableIconCell,
  TableDots,
  Pagination,
  Chip,
  ContainerAvatar,
  enqueueSnackbar,
  managesalesDeleteModal,
  managesalesEditModal,
  TableIconsCell,
  Checkbox,
  Button,
  shareImagesModal,
  SimpleTable,
  containerListModal,
} from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { Filters } from './Filters';
import { useStore } from '../../context';
import { momentTextFormat, showToRoles, formatContainerType, filterWithUserId } from '../../utils';
import { getNotFounds } from './utils';
import {
  useStatesWithFilterCounter,
  useSort,
  usePaginationState,
  useSalesQuery,
  clearSavedFilters,
  useSaveFilters,
  useIsSmallScreen,
} from '../../hooks';
import {
  TABLE,
  SaleStatusToChipLabels,
  SaleStatusToChipColors,
  MESSAGES,
  managesalesTableDotsActions,
  imageActions,
  ContainerConditionToChipLabels,
  ContainerConditionToChipColors,
} from '../../const';
import { FeeType, SaleGetAllQuery } from '../../types';
import { containersService } from '../../services';
import { i18n } from '../../i18n';

let openContainerListPopup: any = () => null;
let cancelContainerList: any = () => null;

interface ManageSalesPageProps {
  innerScroll?: boolean;
}

export const ManageSalesPage = styled.div<ManageSalesPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const ManageSalesHeader = ({ totalCount, containerList, setContainerList }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const isSmallScreen = useIsSmallScreen();

  const handlePersonalShopClick = () => {
    window.open(`/marketplace/${user.clientEmployee.client.code}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.manageSales.title', { totalCount })}
      </Typography>
      {!isSmallScreen && containerList && (
        <Button
          size={'medium'}
          onClick={() => {
            cancelContainerList();
            setContainerList();
          }}
          data-color={'white'}
        >
          {i18n.t('button.cancel')}
        </Button>
      )}
      {!isSmallScreen && !containerList && (
        <Button size={'medium'} onClick={openContainerListPopup}>
          {i18n.t('button.containerList')}
        </Button>
      )}
      {showToRoles('client', user) && (
        <Button size={'medium'} onClick={handlePersonalShopClick}>
          {i18n.t('button.personalShop')}
        </Button>
      )}
    </DashboardHeader>
  );
};

interface CheckboxActionsProps {
  data: SaleGetAllQuery['data'];
  refetch: () => void;
  checkIndexs: number[];
  setCheckIndexs: (indexes: number[]) => void;
}

export const CheckboxActions = ({
  data,
  checkIndexs,
  setCheckIndexs,
  refetch,
}: CheckboxActionsProps) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const handleClickAction = (val: any) => {
    if (val === 'edit') {
      const validatedData = data
        .filter((_: any, i: number) => checkIndexs.includes(i))
        .filter((item) => item.sale.status === 'FOR_SALE');

      if (!validatedData.length) {
        return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
      }

      managesalesEditModal.data = validatedData.map((item) => ({
        id: item.id,
        number: item.number,
        type: item.type,
        price: item.sale.ownerSetPrice,
        allowCounterOffers: item.sale.allowCounterOffers,
        description: item.sale.description,

        feeType: item.sale.feeType,
        feeValue: item.sale.feeValue,
      }));

      managesalesEditModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.MANAGE_SALES.SUCCESS.EDIT, 'success');
        refetch();
      };
      managesalesEditModal.open();
    }
    if (val === 'delete') {
      const validatedData = data
        .filter((_: any, i: number) => checkIndexs.includes(i))
        .filter((item) => item.sale.status === 'FOR_SALE');

      if (!validatedData.length) {
        return enqueueSnackbar(MESSAGES.ACTIONS.NOT_VALID, 'warning');
      }

      managesalesDeleteModal.data = validatedData.map((item) => ({
        id: item.id,
        number: item.number,
      }));

      managesalesDeleteModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.MARKETPLACE.SUCCESS.DELETE, 'success');
        refetch();
      };
      managesalesDeleteModal.open();
    }
  };

  if (!checkIndexs.length) return <></>;

  return (
    <>
      <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('edit')}>
        {i18n.t('button.edit')}
      </Button>
      {showToRoles(
        ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'CLIENT_MANAGER', 'CLIENT_DISPATCHER'],
        user,
      ) && (
        <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('delete')}>
          {i18n.t('dropdown.stopSelling')}
        </Button>
      )}
    </>
  );
};

export const ManageSalesBody = ({
  setData,
  setTotalCount,
  containerList,
  setContainerList,
  checkIndexs,
  setCheckIndexs,
  handleCheckboxChange,
  handleClickImage,
}: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const isViewer = showToRoles(['YARD_COMPANY_VIEWER'], user);
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    yardId: '',
    clientId: '',
    number: '',
    type: 'All',
    condition: 'All',
    status: 'All',
  });
  const { sortField, sortDirection, handleSort } = useSort({
    sortField: 'forSaleDate',
    sortDirection: 'desc',
  });
  const [filterKey, setFilterKey] = useSaveFilters('filters-key', true, Math.random());

  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useSalesQuery({
    ...filterWithUserId(filteredStates, user),
    take,
    skip,
    sortField,
    sortDirection,
  });

  useEffect(() => {
    let oldContainerList = containerList;
    if (isFetching) return;

    if (!oldContainerList && typeof filteredStates.number !== 'string') {
      oldContainerList = filteredStates.number;
      setContainerList(oldContainerList);
    }
    setTotalCount(
      oldContainerList ? getNotFounds(data, oldContainerList).totalCount : data.totalCount,
    );
    setData((oldContainerList ? getNotFounds(data, oldContainerList) : data).data);
  }, [isFetching]);

  useEffect(() => {
    openContainerListPopup = () => {
      const fc = (fcProps = { containerNumber: [] }) => {
        const containerList = fcProps.containerNumber;
        if (!containerList.length) return;

        clearSavedFilters();
        setContainerList(containerList);
        setFilterKey(Math.random());
        setCheckIndexs([]);
        setStates('cancelall', {
          status: 'All',
          number: containerList,
        });
      };
      containerListModal.fc = fc;
      containerListModal.isFilterList = true;
      containerListModal.open();
    };
    cancelContainerList = () => {
      clearSavedFilters();
      setFilterKey(Math.random());
      setCheckIndexs([]);
      setStates('cancelall');
    };
  }, []);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleOnPageChange = (e: any, page: number) => {
    if (checkIndexs.length) setCheckIndexs([]);
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (row: any, number: any, val: any, id: any) => {
    if (val === 'delete') {
      managesalesDeleteModal.data = [{ id, number }];
      managesalesDeleteModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.MARKETPLACE.SUCCESS.DELETE, 'success');
        refetch();
      };
      managesalesDeleteModal.open();
    }
    if (val === 'edit') {
      managesalesEditModal.data = [
        {
          id,
          number,
          type: row.type,
          price: row.sale.ownerSetPrice,
          allowCounterOffers: row.sale.allowCounterOffers,
          description: row.sale.description,

          feeType: row.sale.feeType,
          feeValue: row.sale.feeValue,
        },
      ];

      managesalesEditModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.MANAGE_SALES.SUCCESS.EDIT, 'success');
        refetch();
      };
      managesalesEditModal.open();
    }
    if (val === 'download-images') {
      containersService.downloadImages(row.images, number);
    }
    if (val === 'share-images') {
      shareImagesModal.images = row.images;
      shareImagesModal.containerNumber = number;
      shareImagesModal.open();
    }
  };

  return (
    <SimpleTable
      data={containerList ? getNotFounds(data, containerList) : data}
      filters={() => <Filters key={filterKey} setStates={handleFilterChange} {...states} />}
      checkboxActions={
        isViewer
          ? undefined
          : (data) => (
              <CheckboxActions
                checkIndexs={checkIndexs.filter((i: number) => data[i].sale.status === 'FOR_SALE')}
                data={data}
                refetch={refetch}
                setCheckIndexs={setCheckIndexs}
              />
            )
      }
      tableRowProps={(container, i) => ({
        key: container.number + i,
        onClick: () => {},
      })}
      columns={[
        {
          column: 1,
          head: () => (
            <TableHeadCell align={'left'} withGap>
              {!isViewer && (
                <Checkbox
                  indeterminate={
                    !!checkIndexs.length &&
                    checkIndexs.length !== getNotFounds(data, containerList).data.length
                  }
                  checked={checkIndexs.length === getNotFounds(data, containerList).data.length}
                  onChange={() => handleCheckboxChange()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {i18n.t('label.actions')}
            </TableHeadCell>
          ),
          body: (container, i) => (
            <TableIconsCell component={'th'} align={'left'}>
              {!isViewer && (
                <Checkbox
                  checked={checkIndexs.includes(i)}
                  onChange={() => handleCheckboxChange(i)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {container.sale.status !== 'NOT_FOUND' && (
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={
                    containerList || isViewer
                      ? imageActions
                      : [
                          ...(container.sale.status === 'FOR_SALE'
                            ? showToRoles(
                                [
                                  'SUPER_ADMIN',
                                  'YARD_COMPANY_ADMIN',
                                  'CLIENT_MANAGER',
                                  'CLIENT_DISPATCHER',
                                ],
                                user,
                              )
                              ? managesalesTableDotsActions
                              : [managesalesTableDotsActions[0]]
                            : []),
                          ...imageActions,
                        ]
                  }
                  onSelect={(val) => {
                    handleDotsOptionSelect(container, container.number, val, container.id);
                  }}
                />
              )}
            </TableIconsCell>
          ),
        },
        {
          headText: () => i18n.t('label.photo'),
          body: (container) => (
            <TableIconCell component={'th'} align={'left'}>
              <ContainerAvatar
                images={container.images}
                onClick={() => handleClickImage(container.images)}
              />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('common.client'),
          bodyText: (container) => container.clientYard?.client?.code,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.containerNumber'),
          bodyText: (container) => container.number,
        },
        {
          headText: () => i18n.t('label.type'),
          bodyText: (container) => formatContainerType(container.type),
        },
        {
          headText: () => i18n.t('label.condition'),
          body: (container) => (
            <TableCell align={'left'}>
              {container.condition && (
                <Chip
                  size={'small'}
                  label={ContainerConditionToChipLabels[container.condition]}
                  color={ContainerConditionToChipColors[container.condition]}
                />
              )}
            </TableCell>
          ),
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'finalPrice'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.price')}
            </TableHeadCell>
          ),
          bodyText: (container) => `${container.sale.ownerSetPrice}$`,
        },
        {
          headText: () => i18n.t('label.fee'),
          bodyText: (container) =>
            `${container.sale.feeValue}${container.sale.feeType === FeeType.FIXED ? '$' : '%'}`,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.finalPrice'),
          bodyText: (container) => `${container.sale.finalPrice}$`,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.status'),
          body: (container) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={SaleStatusToChipLabels[container.sale.status]}
                color={SaleStatusToChipColors[container.sale.status]}
              />
            </TableCell>
          ),
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'forSaleDate'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.forSaleDate')}
            </TableHeadCell>
          ),
          bodyText: (container) =>
            container.sale.forSaleDate ? momentTextFormat(container.sale.forSaleDate, user) : '',
        },
        {
          headText: () => i18n.t('label.createdBy'),
          bodyText: (container) => container.sale.createdByName,
          isHidden: () => !showToRoles(['SUPER_ADMIN'], user),
        },
      ]}
      footer={
        containerList
          ? undefined
          : () => (
              <TableWrapperFooter>
                <Pagination
                  count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
                  page={page}
                  onChange={handleOnPageChange}
                />
              </TableWrapperFooter>
            )
      }
    />
  );
};
